/**
 * Pullman Bookings — the counting-variant dropdown shared by every sub-view.
 *
 *   As MERAC reports (default) — the cached rows exactly as Colin's DN_001 emits
 *     them: a booking that spans more than one carriage is repeated (and its
 *     course quantities multiplied) once per carriage.
 *   One count per booking — each booking counted once, its people placed in the
 *     carriage their seats are in, its courses shared across those carriages.
 *
 * The value rides on every fetch as `variant=merac|booking`; the API does the
 * de-duplication (api/src/bookingDedup.ts) and returns a `dedup` summary that
 * the note under the dropdown reports.
 */
export type CountVariant = "merac" | "booking";

export interface DedupInfo {
  multi_carriage: number;
  unattributed: { booking: number; journey_date: string; carriage: string }[];
  left_as_reported: { booking: number; journey_date: string }[];
}

export const VARIANT_OPTIONS: { value: CountVariant; label: string }[] = [
  { value: "merac", label: "As MERAC reports (default)" },
  { value: "booking", label: "One count per booking" },
];

export function variantLabel(v: CountVariant): string {
  return VARIANT_OPTIONS.find((o) => o.value === v)?.label ?? v;
}

/** `&variant=…` for a fetch URL. */
export function variantParam(v: CountVariant): string {
  return `&variant=${v}`;
}

/** The one-line explanation under the dropdown (plus what the server de-duplicated). */
export function variantNote(v: CountVariant, info?: DedupInfo | null): string {
  if (v === "merac") {
    return "Exactly what MERAC's report shows: a booking spread over more than one carriage is counted once per carriage (its seats, covers and courses repeated on every carriage), so such bookings are over-counted.";
  }
  let s = "Each booking counted once: its people go in the carriage their seats are in, and its course choices are shared across those carriages in proportion to the seats in each (a dish can't be tied to a seat).";
  if (info) {
    const n = info.multi_carriage;
    s += n ? ` ${n} multi-carriage booking${n === 1 ? "" : "s"} counted once in this range.` : " No multi-carriage bookings in this range.";
    if (info.unattributed.length) {
      s += ` Seats couldn't be split by carriage for ${info.unattributed
        .map((u) => `booking ${u.booking} (${u.journey_date}) — whole party shown in ${u.carriage.replace(/^Pullman\s*-\s*/i, "")}`)
        .join("; ")}.`;
    }
    if (info.left_as_reported.length) {
      s += ` Left as MERAC reports (carriage rows differ): ${info.left_as_reported.map((u) => `booking ${u.booking} (${u.journey_date})`).join(", ")}.`;
    }
  }
  return s;
}

export function VariantSelect({ value, onChange }: { value: CountVariant; onChange: (v: CountVariant) => void }) {
  return (
    <label className="bookings-field bookings-variant">
      <span>Counting</span>
      <select value={value} onChange={(e) => onChange(e.target.value as CountVariant)} title="How a booking spread over more than one carriage is counted">
        {VARIANT_OPTIONS.map((o) => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
    </label>
  );
}
